<!-- Cart -->
<!--
  TICKET DETAILS card: literal card
  border/radius, blue/gray/light-blue accents on the breakdown grid, and a
  two-tone total line. Style/layout only — same bindings/copy/data-cy hooks
  as before; the copy stays "Your Ticket Details".

  The heading and the grid's section/row/seats numbers use `text-main-blue` (a
  fixed brand blue), not `text-main` (the club's own theme colour) — same
  fixed-blue convention as the seat-selection sidebar's own cart
  (cart-section-item.component.html). `text-main` would instead follow each
  club's own `default-color`, which for some tenants (e.g. Washington
  Nationals) isn't blue at all.
-->
<div
  id="ticket-details"
  class="md:flex md:flex-col md:justify-between bg-white shadow-[0px_3px_6px_0px_rgba(0,0,0,0.16)] p-4 border border-[#F2F2F2] rounded-3xl w-full h-fit">
  <div>
    <!--
      `px-2 md:px-4` here MUST match the table wrapper's own `px-2 md:px-4` below
      (no `m-2`/extra nested padding on either side) so the "Your Ticket Details"
      heading lines up with the grid's left edge instead of sitting ~24px further
      right than the section/row/seats columns it introduces.
    -->
    <div class="flex items-center px-2 md:px-8 pt-2">
      <div class="w-full md:text-left text-center">
        <!--
          `text-main-blue` (fixed brand blue, not the club's own `text-main` theme
          colour): matches the same fixed-blue convention already used for seat/section
          numbers in the seat-selection sidebar's own cart (cart-section-item.component.html),
          which this "Your Ticket Details" grid mirrors. `text-main` would instead follow each
          club's own `default-color` (e.g. black for Washington Nationals), missing the Figma
          blue entirely for any tenant whose theme isn't itself blue.
        -->
        <h3 class="mb-1 font-extrabold text-main-blue text-xl">
          Your Ticket Details
        </h3>
        <p class="font-semibold text-gray-400">
          {{ transaction().transaction.event?.description }} -
          @if (showDateAsTbd) {
            {{ eventDateTbdText }}
          } @else {
            {{ transaction().transaction.event?.date | date: 'short' }}
          }
        </p>
      </div>
    </div>
    <div class="px-2 md:px-4">
      <div class="w-full">
        <table class="w-full border-separate table-fixed">
          @for (section of seatsForTable(); track section.id) {
            <tr class="flex flex-wrap md:flex-nowrap my-2 mb-3 md:mb-0">
              <td class="content-center mb-1 md:mb-0 w-full md:w-16 td">
                <div class="flex justify-center items-center">
                  <img
                    ngSrc="/assets/img/chair-blue-icon.svg"
                    alt="main chair icon"
                    width="12"
                    height="12"
                    [priority]="false"
                    class="mr-1" />
                  <p class="font-bold text-main-blue text-center">
                    {{ section.totalSectionSeats }}
                  </p>
                </div>
              </td>
              <td
                class="flex justify-center md:mx-2 mb-1 md:mb-0 w-full md:w-48 md:shrink-0 td">
                <p class="flex items-center text-center">
                  Section
                  <span class="ml-2 font-bold text-main-blue">{{
                    section.id
                  }}</span>
                </p>
              </td>
              <td class="p-0 w-full">
                @for (row of section.rows; track row.id) {
                  <div
                    class="flex flex-wrap md:flex-nowrap gap-2 mb-1 last:mb-0">
                    <p class="flex items-center td-row">
                      Row<span class="ml-2 font-bold text-main-blue">{{
                        row.id
                      }}</span>
                    </p>
                    <p class="flex flex-1 items-center td-seats">
                      Seats<span class="ml-2 font-bold text-main-blue">{{
                        row.seatsNumbers.join(', ')
                      }}</span>
                    </p>
                  </div>
                }
              </td>
            </tr>
          }
        </table>
      </div>
      <!--
        Per-price-group lines, listed below the
        section/row/seats grid (subtotals after the seat breakdown).
        A "+ fees per ticket $X" suffix is intentionally NOT rendered —
        there is no per-ticket fee binding on this component today.
      -->
      <!--
        `px-2 md:px-4` here MUST match the grid's own wrapper padding above (same
        reasoning as the heading) so these lines' left edge lines up with the
        section/row/seats grid instead of sitting further right under `md:px-8`.
        `text-main-blue` on the quantity/price spans, not `text-main`: same
        fixed-blue convention as "Total Balance" (order-summary.component.html)
        and the grid's own numbers above.
      -->
      <div class="px-2 md:px-4 mt-4">
        @for (seat of seatsByPrice(); track $index) {
          <h4 class="font-bold md:text-left text-center">
            <span data-cy="cart-seats-quantity" class="text-main-blue">{{
              seat.quantity
            }}</span>
            Seats x
            <span data-cy="cart-buyer-type">{{
              transaction().transaction.buyerTypeName
            }}</span>
            at
            <span class="text-main-blue"
              >${{ seat.price | number: '1.2-2' }}</span
            >
          </h4>
        }
      </div>
    </div>
  </div>
  <div class="flex justify-end items-center p-5 border-t border-t-gray-100">
    @if (transaction().transaction.tdcTransaction?.totalPriceSeats) {
      <h5 class="flex items-baseline gap-1 font-bold">
        <span class="text-xs">Total Price:</span>
        <span class="text-main text-base">
          ${{
            transaction().transaction.tdcTransaction?.totalPriceSeats
              | number: '1.2-2'
          }}
        </span>
      </h5>
    }
  </div>
</div>
